import React, { useState, useMemo, useEffect } from 'react';
import {
  FaTable,
  FaDownload,
  FaCopy,
  FaCode,
  FaSortUp,
  FaSortDown,
  FaChevronLeft,
  FaChevronRight,
  FaChartBar,
  FaChartLine,
  FaChartPie,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { toApiDateString } from '../../../../utils/dateUtils';
import ThemedSelect from '../../../common/ThemedSelect';
import { useThemeTokens } from '../../../../utils/themeTokens';

// Same eight hues in both themes: deep 800/900 on light, 400-level on dark so the
// series stay legible against a dark surface (ST-00127).
const CHART_COLORS_LIGHT = [
  '#312e81',
  '#075985',
  '#065f46',
  '#92400e',
  '#991b1b',
  '#5b21b6',
  '#9d174d',
  '#115e59',
];
const CHART_COLORS_DARK = [
  '#818cf8',
  '#38bdf8',
  '#34d399',
  '#fbbf24',
  '#f87171',
  '#a78bfa',
  '#f472b6',
  '#2dd4bf',
];

const QueryResults = ({ results }) => {
  const t = useThemeTokens();
  const CHART_COLORS = t.isDark ? CHART_COLORS_DARK : CHART_COLORS_LIGHT;
  const [viewMode, setViewMode] = useState('table');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;

  const [chartType, setChartType] = useState('bar');
  const [labelColumn, setLabelColumn] = useState(null);
  const [valueColumns, setValueColumns] = useState([]);

  const hasResults = results && results.data && Array.isArray(results.data);
  const data = hasResults ? results.data : [];
  const meta = hasResults ? results.meta : {};
  const columns = data.length > 0 ? Object.keys(data[0]) : [];

  const columnAnalysis = useMemo(() => {
    if (!data.length) return { stringCols: [], numericCols: [], dateCols: [] };
    const stringCols = [];
    const numericCols = [];
    const dateCols = [];

    columns.forEach((col) => {
      const sampleValues = data
        .slice(0, 20)
        .map((r) => r[col])
        .filter((v) => v != null);
      if (sampleValues.length === 0) return;

      if (sampleValues.every((v) => typeof v === 'number')) {
        numericCols.push(col);
        return;
      }
      if (sampleValues.every((v) => /^\d{4}-\d{2}-\d{2}/.test(String(v)))) {
        dateCols.push(col);
        return;
      }
      stringCols.push(col);
    });

    return { stringCols, numericCols, dateCols };
  }, [data, columns]);

  useEffect(() => {
    const { stringCols, numericCols, dateCols } = columnAnalysis;
    const defaultLabel = dateCols[0] || stringCols[0] || columns[0];
    setLabelColumn(defaultLabel);
    setValueColumns(numericCols.slice(0, 5));
    if (dateCols.length > 0) setChartType('line');
    else if (numericCols.length === 1 && stringCols.length > 0 && data.length <= 10)
      setChartType('pie');
    else setChartType('bar');
  }, [columnAnalysis, columns, data.length]);

  const sortedData = useMemo(() => {
    if (!sortColumn) return data;
    return [...data].sort((a, b) => {
      const valA = a[sortColumn];
      const valB = b[sortColumn];
      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;
      if (typeof valA === 'number' && typeof valB === 'number') {
        return sortDirection === 'asc' ? valA - valB : valB - valA;
      }
      const strA = String(valA);
      const strB = String(valB);
      return sortDirection === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
    });
  }, [data, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const pageData = sortedData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const exportCSV = () => {
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [
      columns.join(','),
      ...data.map((row) => columns.map((c) => escape(row[c])).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `query_results_${toApiDateString(new Date())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  const copyResults = () => {
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [
      columns.join(','),
      ...data.map((row) => columns.map((c) => escape(row[c])).join(',')),
    ].join('\n');
    navigator.clipboard.writeText(csv).then(() => toast.success('Copied to clipboard'));
  };

  const formatValue = (val) => {
    if (val === null || val === undefined)
      return <span className="text-gray-400 italic">null</span>;
    if (typeof val === 'boolean')
      return <span className={val ? 'text-emerald-600' : 'text-red-500'}>{String(val)}</span>;
    if (typeof val === 'number')
      return <span className="tabular-nums">{val.toLocaleString()}</span>;
    const str = String(val);
    if (str.length > 120) return <span title={str}>{str.slice(0, 120)}...</span>;
    return str;
  };

  const chartData = useMemo(() => {
    if (!labelColumn || valueColumns.length === 0) return [];
    const rows = data.slice(0, 200).map((row) => {
      const entry = { _label: row[labelColumn] != null ? String(row[labelColumn]) : '' };
      valueColumns.forEach((col) => {
        entry[col] = typeof row[col] === 'number' ? row[col] : 0;
      });
      return entry;
    });
    if (chartType === 'pie') {
      const aggregated = {};
      rows.forEach((r) => {
        aggregated[r._label] = (aggregated[r._label] || 0) + (r[valueColumns[0]] || 0);
      });
      let entries = Object.entries(aggregated)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
      if (entries.length > 15) {
        const top = entries.slice(0, 14);
        const otherVal = entries.slice(14).reduce((s, e) => s + e.value, 0);
        entries = [...top, { name: 'Other', value: otherVal }];
      }
      return entries;
    }
    return rows;
  }, [data, labelColumn, valueColumns, chartType]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-surface border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
        <p className="font-medium text-gray-900 mb-1">{label}</p>
        {payload.map((entry, i) => (
          <p key={i} className="text-gray-600">
            <span
              className="inline-block w-2 h-2 rounded-full mr-1.5"
              style={{ backgroundColor: entry.color }}
            />
            {entry.name}: {Number(entry.value).toLocaleString()}
          </p>
        ))}
      </div>
    );
  };

  const renderChart = () => {
    if (columnAnalysis.numericCols.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <FaChartBar className="text-3xl mb-3 text-gray-300" />
          <p className="text-sm font-medium">No numeric columns available for charting</p>
          <p className="text-xs mt-1">Query results need at least one numeric column</p>
        </div>
      );
    }

    if (chartData.length === 0) return null;

    const axisStyle = { fontSize: 11, fill: t.axisLabel };
    const gridStyle = { strokeDasharray: '3 3', stroke: t.gridSubtle };

    if (chartType === 'pie') {
      return (
        <ResponsiveContainer width="100%" height={350}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={130}
              innerRadius={50}
              paddingAngle={2}
              strokeWidth={1}
              stroke={t.surface}
            >
              {chartData.map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="circle"
              iconSize={8}
              formatter={(value) => <span className="text-xs text-gray-600">{value}</span>}
            />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    const ChartComponent = chartType === 'line' ? LineChart : BarChart;

    return (
      <ResponsiveContainer width="100%" height={350}>
        <ChartComponent data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
          <CartesianGrid {...gridStyle} />
          <XAxis
            dataKey="_label"
            tick={axisStyle}
            tickLine={false}
            axisLine={{ stroke: t.grid }}
            interval={chartData.length > 30 ? Math.floor(chartData.length / 15) : 0}
            angle={chartData.length > 15 ? -45 : 0}
            textAnchor={chartData.length > 15 ? 'end' : 'middle'}
            height={chartData.length > 15 ? 60 : 30}
          />
          <YAxis
            tick={axisStyle}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)}
          />
          <Tooltip content={<CustomTooltip />} />
          {valueColumns.length > 1 && (
            <Legend
              iconType="circle"
              iconSize={8}
              formatter={(value) => <span className="text-xs text-gray-600">{value}</span>}
            />
          )}
          {valueColumns.map((col, i) =>
            chartType === 'line' ? (
              <Line
                key={col}
                type="monotone"
                dataKey={col}
                stroke={CHART_COLORS[i % CHART_COLORS.length]}
                strokeWidth={2}
                dot={chartData.length <= 30}
                activeDot={{ r: 4 }}
              />
            ) : (
              <Bar
                key={col}
                dataKey={col}
                fill={CHART_COLORS[i % CHART_COLORS.length]}
                radius={[2, 2, 0, 0]}
                maxBarSize={60}
              />
            )
          )}
        </ChartComponent>
      </ResponsiveContainer>
    );
  };

  if (!hasResults) return null;

  return (
    <div className="bg-surface rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center gap-4">
          <h3 className="text-sm font-semibold text-gray-900">Results</h3>
          <div className="flex items-center gap-3 text-[11px] text-gray-500">
            <span>
              {data.length.toLocaleString()} row{data.length !== 1 ? 's' : ''}
            </span>
            <span>
              {columns.length} col{columns.length !== 1 ? 's' : ''}
            </span>
            {meta?.executionTime && <span>{meta.executionTime}ms</span>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <div className="flex items-center rounded-md border border-gray-200 bg-surface overflow-hidden mr-2">
            <button
              onClick={() => setViewMode('table')}
              className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${
                viewMode === 'table'
                  ? 'bg-gray-900 dark:bg-gray-50 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FaTable className="inline mr-1 text-[9px]" />
              Table
            </button>
            <button
              onClick={() => setViewMode('chart')}
              className={`px-2.5 py-1 text-[11px] font-medium border-l border-gray-200 transition-colors ${
                viewMode === 'chart'
                  ? 'bg-gray-900 dark:bg-gray-50 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FaChartBar className="inline mr-1 text-[9px]" />
              Chart
            </button>
            <button
              onClick={() => setViewMode('json')}
              className={`px-2.5 py-1 text-[11px] font-medium border-l border-gray-200 transition-colors ${
                viewMode === 'json'
                  ? 'bg-gray-900 dark:bg-gray-50 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FaCode className="inline mr-1 text-[9px]" />
              JSON
            </button>
          </div>
          <button
            onClick={copyResults}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            title="Copy"
          >
            <FaCopy className="text-xs" />
          </button>
          <button
            onClick={exportCSV}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            title="Export CSV"
          >
            <FaDownload className="text-xs" />
          </button>
        </div>
      </div>

      {/* Table View */}
      {viewMode === 'table' && (
        <>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gray-50 border-b border-gray-200">
                  {columns.map((col) => (
                    <th
                      key={col}
                      onClick={() => handleSort(col)}
                      className="text-left px-3 py-2 text-[11px] font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 hover:bg-gray-100 transition-colors whitespace-nowrap select-none"
                    >
                      <span className="inline-flex items-center gap-1">
                        {col}
                        {sortColumn === col &&
                          (sortDirection === 'asc' ? (
                            <FaSortUp className="text-primary-500" />
                          ) : (
                            <FaSortDown className="text-primary-500" />
                          ))}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {pageData.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                    {columns.map((col) => (
                      <td
                        key={col}
                        className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap max-w-xs truncate"
                      >
                        {formatValue(row[col])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 bg-gray-50/30">
              <span className="text-[11px] text-gray-500">
                {(currentPage - 1) * rowsPerPage + 1}–
                {Math.min(currentPage * rowsPerPage, sortedData.length)} of {sortedData.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-colors"
                >
                  <FaChevronLeft className="text-[10px]" />
                </button>
                <span className="text-[11px] text-gray-500 px-2">
                  {currentPage}/{totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-colors"
                >
                  <FaChevronRight className="text-[10px]" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Chart View */}
      {viewMode === 'chart' && (
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-500 font-medium">Type:</span>
              <div className="flex rounded-md border border-gray-200 bg-surface overflow-hidden">
                {[
                  { type: 'bar', icon: FaChartBar },
                  { type: 'line', icon: FaChartLine },
                  { type: 'pie', icon: FaChartPie },
                ].map(({ type, icon: Icon }) => (
                  <button
                    key={type}
                    onClick={() => setChartType(type)}
                    className={`px-2 py-1 text-[11px] font-medium ${type !== 'bar' ? 'border-l border-gray-200' : ''} ${
                      chartType === type
                        ? 'bg-gray-900 dark:bg-gray-50 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="inline mr-0.5 text-[9px]" />
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-500 font-medium">Label:</span>
              <ThemedSelect
                value={labelColumn || ''}
                onChange={(e) => setLabelColumn(e.target.value)}
                className="text-xs h-7"
              >
                {columns.map((col) => (
                  <option key={col} value={col}>
                    {col}
                  </option>
                ))}
              </ThemedSelect>
            </div>

            {chartType !== 'pie' && columnAnalysis.numericCols.length > 1 && (
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-gray-500 font-medium">Values:</span>
                <div className="flex flex-wrap gap-1">
                  {columnAnalysis.numericCols.map((col) => (
                    <button
                      key={col}
                      onClick={() =>
                        setValueColumns((prev) =>
                          prev.includes(col)
                            ? prev.length > 1
                              ? prev.filter((c) => c !== col)
                              : prev
                            : [...prev, col]
                        )
                      }
                      className={`px-2 py-0.5 text-[11px] rounded border transition-colors ${
                        valueColumns.includes(col)
                          ? 'bg-primary-50 text-primary-700 border-primary-300'
                          : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {col}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {chartType === 'pie' && columnAnalysis.numericCols.length > 1 && (
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-gray-500 font-medium">Value:</span>
                <ThemedSelect
                  value={valueColumns[0] || ''}
                  onChange={(e) => setValueColumns([e.target.value])}
                  className="text-xs h-7"
                >
                  {columnAnalysis.numericCols.map((col) => (
                    <option key={col} value={col}>
                      {col}
                    </option>
                  ))}
                </ThemedSelect>
              </div>
            )}
          </div>

          <div className="bg-gradient-to-br from-white to-slate-50/50 rounded-lg border border-gray-200/60 p-4">
            {renderChart()}
          </div>

          {data.length > 200 && (
            <p className="text-[11px] text-gray-400 text-center">
              Showing first 200 of {data.length.toLocaleString()} rows
            </p>
          )}
        </div>
      )}

      {/* JSON View */}
      {viewMode === 'json' && (
        <div className="max-h-[400px] overflow-auto">
          <pre className="p-4 bg-[#282c34] text-[#abb2bf] text-xs font-mono leading-relaxed whitespace-pre-wrap">
            {JSON.stringify(pageData, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default QueryResults;
